import React, { useEffect, useState } from 'react';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import configLoader from './config-loader';
import config from './config';
import Login from './components/base/Login';
import Main from './components/base/Main';
import { authenticate } from './services/user-service';
import { checkUserTenants } from './services/tenant-service';
import { setPiiKey } from './helpers/piiCrypto';
import SessionConflictModal from './components/common/SessionConflictModal';
import SessionKickedModal from './components/common/SessionKickedModal';
import TenantSelector from './components/common/TenantSelector';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

function AppContent() {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [validUser, setValidUser] = useState();
  const [idToken, setIdToken] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [authenticating, setAuthenticating] = useState(false);
  const [showSessionConflict, setShowSessionConflict] = useState(false);
  const [showSessionKicked, setShowSessionKicked] = useState(false);
  const [showTenantSelector, setShowTenantSelector] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);

  useEffect(() => {
    const storedKey = sessionStorage.getItem('_pk');
    if (storedKey) setPiiKey(storedKey);
  }, []);

  useEffect(() => {}, []);

  useEffect(() => {
    if (sessionStorage.getItem('_loggingOut')) return;
    if (isAuthenticated && !idToken) {
      requestProfileData();
    }
    if (idToken && idToken !== '') {
      sessionStorage.setItem('AzureToken', idToken);
      sessionStorage.setItem('Token', idToken);
      getUserData();
    }
  }, [isAuthenticated, idToken]);

  async function requestProfileData() {
    const accounts = await instance.getAllAccounts();
    if (accounts.length > 0) {
      const request = { scopes: ['User.Read'], prompt: 'select_account', account: accounts[0] };
      instance
        .acquireTokenSilent(request)
        .then((response) => { setIdToken(response.idToken); })
        .catch(() => {
          instance.acquireTokenPopup(request)
            .then((response) => { setIdToken(response.idToken); })
            .catch(() => { setValidUser(false); setErrorMessage('Failed to acquire token'); });
        });
    } else {
      setValidUser(false);
      setErrorMessage('Unable to login into system.');
    }
  }

  async function getUserData() { // NOSONAR
    if (authenticating) return;
    setAuthenticating(true);
    try {
      const result = await authenticate();

      if (result && result.statusCode === '0000') {
        const userData = result.data;
        sessionStorage.setItem('UserName', userData.userName || '');
        sessionStorage.setItem('Role', userData.role || '');
        sessionStorage.setItem('Token', userData.token);
        sessionStorage.setItem('Email', userData.userId || '');
        sessionStorage.setItem('SessionId', userData.sessionId || '');
        sessionStorage.setItem('AuthorizedSessions', userData.authorizedSessions ? JSON.stringify(userData.authorizedSessions) : '{}');
        sessionStorage.setItem('userData', JSON.stringify(userData));

        if (userData.piiKey) {
          setPiiKey(userData.piiKey);
          sessionStorage.setItem('_pk', userData.piiKey);
        }

        // Check if SYSADMIN — show tenant selector (only if TenantId not already set)
        if (!sessionStorage.getItem('TenantId')) {
          try {
            const tenantResult = await checkUserTenants();
            if (tenantResult.statusCode === '0000' && tenantResult.data.isSysadmin && tenantResult.data.tenants.length > 1) {
              setAvailableTenants(tenantResult.data.tenants);
              setShowTenantSelector(true);
              return;
            } else if (tenantResult.statusCode === '0000' && tenantResult.data.isSysadmin && tenantResult.data.tenants.length === 1) {
              sessionStorage.setItem('TenantId', tenantResult.data.tenants[0].tenantId);
            } else {
              sessionStorage.setItem('TenantId', userData.tenantId || '');
            }
          } catch {
            sessionStorage.setItem('TenantId', userData.tenantId || '');
          }
        }

        setValidUser(true);
      } else if (result && result.statusCode === '4092') {
        setShowSessionConflict(true);
        setValidUser(false);
      } else {
        setValidUser(false);
        setErrorMessage(result?.statusMsg || 'User does not exist');
      }
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setShowSessionConflict(true);
        setValidUser(false);
      } else {
        setValidUser(false);
        setErrorMessage(err.toString());
        sessionStorage.clear();
      }
    }
  }

  const handleSessionTakeover = async () => {
    try {
      const response = await fetch(`${config.REACT_APP_API_ENDPOINT}/login/takeover-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('AzureToken')}`,
        },
      });
      const result = await response.json();
      if (result.statusCode === '0000') {
        const userData = result.data;
        sessionStorage.setItem('UserName', userData.userName || '');
        sessionStorage.setItem('Role', userData.role || '');
        sessionStorage.setItem('Token', userData.token);
        sessionStorage.setItem('Email', userData.userId || '');
        sessionStorage.setItem('SessionId', userData.sessionId || '');
        sessionStorage.setItem('AuthorizedSessions', userData.authorizedSessions ? JSON.stringify(userData.authorizedSessions) : '{}');
        sessionStorage.setItem('userData', JSON.stringify(userData));
        if (userData.piiKey) setPiiKey(userData.piiKey);
        setShowSessionConflict(false);
        setValidUser(true);
      } else {
        setErrorMessage(result.statusMsg || 'Session takeover failed');
        setShowSessionConflict(false);
        setValidUser(false);
      }
    } catch {
      setErrorMessage('Failed to take over session');
      setShowSessionConflict(false);
      setValidUser(false);
    }
  };

  const handleSessionConflictCancel = () => {
    setShowSessionConflict(false);
    setValidUser(false);
    setErrorMessage('Login cancelled');
  };

  const handleSessionKickedLogin = () => {
    setShowSessionKicked(false);
    setValidUser(undefined);
    setIdToken(null);
    sessionStorage.clear();
  };

  useEffect(() => {
    const handleSessionKicked = () => {
      setShowSessionKicked(true);
      setValidUser(false);
      sessionStorage.clear();
    };
    window.addEventListener('sessionKicked', handleSessionKicked);
    return () => { window.removeEventListener('sessionKicked', handleSessionKicked); };
  }, []);

  return (
    <React.Fragment>
      {isAuthenticated ? (
        <div>
          {validUser === true && <Main />}
          {validUser === false && <Login errorMessage={errorMessage} />}
          {validUser === undefined && <div>Loading...</div>}
        </div>
      ) : (
        <div><Login /></div>
      )}

      <SessionConflictModal
        show={showSessionConflict}
        onTakeover={handleSessionTakeover}
        onCancel={handleSessionConflictCancel}
      />

      <SessionKickedModal
        show={showSessionKicked}
        onLogin={handleSessionKickedLogin}
      />

      <TenantSelector
        show={showTenantSelector}
        tenants={availableTenants}
        onSelect={(tenantId) => {
          sessionStorage.setItem('TenantId', tenantId);
          setShowTenantSelector(false);
          setValidUser(true);
        }}
      />
    </React.Fragment>
  );
}

function App() {
  const [msalInstance, setMsalInstance] = useState(null);
  const [msalReady, setMsalReady] = useState(false);

  useEffect(() => {
    configLoader.loadConfig().then(async (runtimeConfig) => {
      Object.assign(config, runtimeConfig);
      const msalConfig = {
        auth: {
          clientId: config.REACT_APP_AD_CLIENT_ID,
          authority: `https://login.microsoftonline.com/${config.REACT_APP_AD_TENANT_ID}`,
          redirectUri: config.REACT_APP_URL,
        },
        cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
      };
      const instance = new PublicClientApplication(msalConfig);
      await instance.initialize();
      await instance.handleRedirectPromise();
      setMsalInstance(instance);
      setMsalReady(true);
    });
  }, []);

  if (!msalReady) return <div>Loading...</div>;

  return (
    <MsalProvider instance={msalInstance}>
      <AppContent />
    </MsalProvider>
  );
}

export default App;
